import type { HTMLAttributes, ReactNode } from 'react';

import { cx } from '../cx';

export type ContainerProps = HTMLAttributes<HTMLDivElement> & {
  /** `page` (por defeito) para no maximo 1920 px; `full` usa o ecra todo (back-office). */
  width?: 'page' | 'full';
};

/** Largura da pagina com as margens laterais certas em cada ecra. */
export function Container({ width = 'page', className, ...props }: ContainerProps) {
  return (
    <div
      className={cx(
        'mx-auto w-full px-4 sm:px-6 lg:px-10 3xl:px-16',
        width === 'page' && 'max-w-page',
        className,
      )}
      {...props}
    />
  );
}

/**
 * Grelha de cartoes (produtos, eventos, passatempos, streamers): 1 coluna no
 * telemovel, 2, 3, 4 a partir de 1280 px e 5 a partir de 1920 px.
 */
export const cardGridClass =
  'grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8 xl:grid-cols-4 3xl:grid-cols-5';

export type SectionHeadingProps = {
  /** «01», «02»… como as secoes de browser.growup.gg. */
  index?: string;
  /** Etiqueta curta em maiusculas («A IDEIA»). */
  label?: ReactNode;
  title: ReactNode;
  /** Uma ou duas frases por baixo do titulo. */
  lead?: ReactNode;
  /** h1 so no topo da pagina; as secoes usam h2 (por defeito). */
  level?: 1 | 2;
  size?: 'xl' | 'l' | 'm';
  className?: string;
};

const titleSizes = { xl: 'text-display-xl', l: 'text-display-l', m: 'text-display-m' };

export function SectionHeading({
  index,
  label,
  title,
  lead,
  level = 2,
  size = 'l',
  className,
}: SectionHeadingProps) {
  const Heading = level === 1 ? 'h1' : 'h2';
  return (
    // O titulo grande do topo pode ocupar mais largura; o texto fica sempre na
    // largura de leitura.
    <header
      className={cx('flex flex-col gap-4', size === 'xl' ? 'max-w-5xl' : 'max-w-prose', className)}
    >
      {index || label ? (
        <p className="flex items-center gap-3 font-sub text-label font-bold uppercase text-gu-ink-muted">
          {index ? <span className="text-gu-accent">{index}</span> : null}
          {index && label ? <span aria-hidden="true" className="h-0.5 w-6 bg-gu-accent" /> : null}
          {label}
        </p>
      ) : null}
      <Heading className={cx('font-display uppercase text-gu-ink', titleSizes[size])}>
        {title}
      </Heading>
      {lead ? <p className="max-w-prose text-body-l text-gu-ink-muted">{lead}</p> : null}
    </header>
  );
}

/** As tres barras inclinadas das pontas do cachecol. No maximo um conjunto por ecra. Medidas no tokens.json (slash-*). */
export function Slashes({ className }: { className?: string }) {
  return (
    <span aria-hidden="true" className={cx('inline-flex gap-[var(--gu-slash-gap)]', className)}>
      <span className="gu-slant h-[var(--gu-slash-h)] w-[var(--gu-slash-w)] bg-gu-accent" />
      <span className="gu-slant h-[var(--gu-slash-h)] w-[var(--gu-slash-w)] bg-gu-ink" />
      <span className="gu-slant h-[var(--gu-slash-h)] w-[var(--gu-slash-w)] bg-gu-accent" />
    </span>
  );
}

export type DisclosureProps = {
  summary: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  className?: string;
};

/** Pergunta e resposta que abre com «+» (perguntas frequentes). Usa <details>: funciona sem JS. */
export function Disclosure({ summary, children, defaultOpen, className }: DisclosureProps) {
  return (
    <details
      open={defaultOpen}
      className={cx(
        'group border-b border-gu-line py-4 [&_summary::-webkit-details-marker]:hidden',
        className,
      )}
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-title font-semibold text-gu-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gu-focus">
        {summary}
        <span
          aria-hidden="true"
          className="font-display text-headline text-gu-accent transition-transform duration-150 group-open:rotate-45"
        >
          +
        </span>
      </summary>
      <div className="pt-3 text-body text-gu-ink-muted">{children}</div>
    </details>
  );
}
